
import React from "react";
import { useAuth } from "@/contexts/auth-context";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useDashboard } from "@/hooks/use-dashboard";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, User, AtSign, Globe, Github, Twitter } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

export default function Profile() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const {
    activeTab,
    setActiveTab,
    walletData,
    loadingWallet,
  } = useDashboard();
  const { toast } = useToast();
  
  const [formData, setFormData] = React.useState({
    name: "",
    bio: "",
    website: "",
    github: "",
    twitter: "",
  });
  
  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        bio: user.bio || "",
        website: (user as any).website || "",
        github: user.githubUsername || "",
        twitter: (user as any).twitterUsername || "",
      });
    }
  }, [user]);
  
  React.useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would update the user's profile
    toast({
      title: "Profile Updated",
      description: "Your profile has been updated successfully."
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!user) return null;
  
  // Prepare the walletData for DashboardLayout
  const formattedWalletData = walletData ? {
    balance: walletData.balance,
    agentsPurchased: 0,
    lastPurchase: 'No purchases yet'
  } : {
    balance: 0,
    agentsPurchased: 0,
    lastPurchase: 'No purchases yet'
  };

  // Safely access potential website property
  const website = (user as any).website;

  return (
    <DashboardLayout 
      user={user}
      isLoading={loading}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      walletData={formattedWalletData}
      loadingWallet={loadingWallet}
    >
      <div className="container mx-auto p-4 md:p-6">
        <h1 className="text-3xl font-bold mb-6">Profile</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <Card>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src="/placeholder.svg" alt={user.name} />
                    <AvatarFallback className="text-2xl">{user.name?.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </div>
                <CardTitle>{user.name}</CardTitle>
                <CardDescription>{user.email}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {user.bio && (
                    <p className="text-muted-foreground">{user.bio}</p>
                  )}
                  <div className="flex items-center">
                    <AtSign className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>Member since {new Date(user.createdAt || Date.now()).toLocaleDateString()}</span>
                  </div>
                  {website && (
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                      <a href={website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Edit Profile</CardTitle>
                <CardDescription>
                  Update your personal information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="name">
                        Display Name
                      </label>
                      <Input 
                        id="name" 
                        name="name"
                        value={formData.name} 
                        onChange={handleInputChange} 
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="bio">
                        Bio
                      </label>
                      <Textarea 
                        id="bio" 
                        name="bio"
                        value={formData.bio} 
                        onChange={handleInputChange} 
                        placeholder="Tell us about yourself"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="website">
                        Website
                      </label>
                      <Input 
                        id="website" 
                        name="website"
                        value={formData.website} 
                        onChange={handleInputChange} 
                        placeholder="https://yourwebsite.com"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="github">
                          Github Username
                        </label>
                        <div className="flex">
                          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm">
                            <Github className="h-4 w-4" />
                          </span>
                          <Input 
                            id="github"
                            name="github" 
                            value={formData.github} 
                            onChange={handleInputChange} 
                            className="rounded-l-none"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="twitter">
                          Twitter Username
                        </label>
                        <div className="flex">
                          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm">
                            <Twitter className="h-4 w-4" />
                          </span>
                          <Input 
                            id="twitter"
                            name="twitter" 
                            value={formData.twitter} 
                            onChange={handleInputChange} 
                            className="rounded-l-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <Button type="submit">Save Changes</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
